import { User } from '../models/User';
import Store from '../wrappers/Store';
/** Badges render at most two, highest priority first. */
export const MAX_TABLE_BADGES = 2;
/**
 * The quiet face editors render (admin canvas, POS edit mode): no
 * operational chrome, just the table's identity.
 */
export const FREE_TABLE_STATE = Object.freeze({
    occupancy: 'free',
    owner: null,
    badges: [],
    ring: false,
    dimmed: false,
    showPrice: false,
    showChip: false,
});
/**
 * The quiet editor face with the selection ring lit — the ONE selected
 * state every layout editor renders (admin canvas, admin beach preview,
 * POS tables, POS sunbeds). Selection is grammar, not host styling.
 */
export const SELECTED_FREE_TABLE_STATE = Object.freeze({
    ...FREE_TABLE_STATE,
    ring: true,
});
/**
 * Owner-chip initials: first letters of the first two words, else the first
 * two characters ("Marko Kovač" → MK, "Ana" → AN).
 */
export function ownerInitials(name) {
    const words = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!words.length)
        return '?';
    const initials = words.length >= 2
        ? `${words[0][0]}${words[1][0]}`
        : words[0].slice(0, 2);
    return initials.toLocaleUpperCase();
}
/**
 * Beach booking lifecycle for a sunbed (moved verbatim from the POS
 * useBeachFloorPlan composable so the grammar lives once — with the date-view
 * flag passed in instead of read from a composable ref).
 *
 * Future dates collapse to available/booked/held; today runs the full
 * machine including the arrival-deadline warning window (30 min before the
 * venue's beach arrival_deadline until the guest checks in).
 */
export function getBookingState(booking, session, viewingFutureDate = false) {
    // Future dates: only booking and hold states are relevant
    if (viewingFutureDate) {
        if (booking?.isHeld())
            return 'held';
        if (booking && !booking.isCheckedOut() && !booking.isNoShow())
            return 'booked';
        return 'available';
    }
    // Today: full state machine
    if (!booking && session)
        return 'walk_in';
    if (!booking)
        return 'available';
    // A surfaced no-show never claims a session: whoever is on the bed now
    // is a walk-in, not the guest who failed to arrive.
    if (booking.isNoShow())
        return session ? 'walk_in' : 'no_show';
    if (booking.isCheckedIn() || session)
        return 'checked_in';
    if (booking.isHeld())
        return 'held';
    if (booking.isCheckedOut())
        return 'available';
    // Confirmed booking — check deadline
    const tenant = Store.get('tenant');
    const arrivalDeadline = tenant?.venue?.settings?.beach?.arrival_deadline;
    if (arrivalDeadline && booking.isConfirmed()) {
        const now = new Date();
        const [hours, minutes] = arrivalDeadline.split(':').map(Number);
        if (isNaN(hours) || isNaN(minutes))
            return 'booked';
        const deadline = new Date();
        deadline.setHours(hours, minutes, 0, 0);
        if (now > deadline)
            return 'deadline_warning';
        const warningTime = new Date(deadline.getTime() - 30 * 60 * 1000);
        if (now > warningTime)
            return 'deadline_warning';
    }
    return 'booked';
}
/** The booking states that surface as badges (identity mapping). */
const BOOKING_BADGE_STATES = new Set([
    'booked', 'deadline_warning', 'no_show', 'held',
]);
/**
 * Derive the semantic visual state for one table/sunbed.
 *
 * Occupancy: orders present → occupied; else an active session or a
 * physically-present booking state (checked_in / walk_in) → seated; else
 * free. Ownership (occupied only) resolves via User.getTableOwner() — the
 * ONE rule, tables and sunbeds alike (the beach any-order heuristic is
 * retired). Limited tables surface neither the order total nor the chip
 * (`showPrice` / `showChip` both false — ownership and seated guests are
 * not shown there). Badge priority: QR requests > booking lifecycle >
 * kitchen ready, capped at MAX_TABLE_BADGES.
 */
export function deriveTableVisualState(input) {
    const orders = input.orders ?? [];
    const isBeach = input.booking !== undefined || input.viewingFutureDate !== undefined
        || input.table.zone_id !== null || input.table.beach_row_id !== null;
    const bookingState = isBeach
        ? getBookingState(input.booking, input.session, input.viewingFutureDate)
        : undefined;
    // --- occupancy (fill channel) ---
    let occupancy = 'free';
    if (orders.length) {
        occupancy = 'occupied';
    }
    else if (input.session || bookingState === 'checked_in' || bookingState === 'walk_in') {
        occupancy = 'seated';
    }
    // --- ownership (chip channel) ---
    let owner = null;
    if (occupancy === 'occupied') {
        const tableOwner = User.getTableOwner(orders);
        if (tableOwner) {
            const me = input.currentUser ?? User.current();
            owner = {
                kind: me && tableOwner.id === me.id ? 'me' : 'other',
                initials: ownerInitials(tableOwner.name),
            };
        }
    }
    // --- badges (attention channel) ---
    const badges = [];
    if ((input.pendingQrCount ?? 0) > 0) {
        badges.push({ kind: 'qr_requests', count: input.pendingQrCount });
    }
    if (bookingState && BOOKING_BADGE_STATES.has(bookingState)) {
        badges.push({ kind: bookingState });
    }
    if (input.kitchenReady) {
        badges.push({ kind: 'kitchen_ready' });
    }
    const isLimited = input.table.type === 'limited';
    return {
        occupancy,
        owner,
        badges: badges.slice(0, MAX_TABLE_BADGES),
        ring: !!input.transferSource,
        dimmed: !!input.disabled,
        showPrice: occupancy === 'occupied' && !isLimited,
        showChip: occupancy !== 'free' && !isLimited,
        bookingState,
    };
}
//# sourceMappingURL=TableVisualState.js.map